import { channelUrl, type ShareChannel, type ShareTarget } from '@tg/shared';
import { toast } from '@/stores/toasts';

/** the channel goes into the link for attribution (`?c=tg`); the server strips it from the canonical URL */
export const withChannel = (url: string, ch: ShareChannel): string => `${url}${url.includes('?') ? '&' : '?'}c=${ch}`;

export const canSystemShare = (): boolean => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * WeChat and QQ get the square card with the invite QR, never a link button (owner decision 30.09): WeChat has no web
 * intent without a Chinese-registered JS-SDK, and QQ's web widget is unreliable on phones.
 */
export const PICTURE_CHANNELS = ['wx', 'qq'] as const;
export type PictureChannel = (typeof PICTURE_CHANNELS)[number];
const isPicture = (ch: ShareChannel): ch is PictureChannel => (PICTURE_CHANNELS as readonly ShareChannel[]).includes(ch);

/** The square card as a File when the browser can share files; fetched ahead so the tap keeps its user activation. */
export async function squareFile(src: string, fileName: string): Promise<File | null> {
  try {
    const blob = await (await fetch(src)).blob();
    const file = new File([blob], fileName, { type: 'image/png' });
    return navigator.canShare?.({ files: [file] }) ? file : null;
  } catch {
    return null;
  }
}

export function downloadImage(src: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = src;
  a.download = fileName;
  a.rel = 'noopener';
  a.click();
}

export interface RunOptions {
  /** download / shared file name (`<brand>.png`) */
  fileName: string;
  /** prefetched square card for the system share (see squareFile) */
  file?: Promise<File | null>;
}

export type RunResult = { picture: PictureChannel } | { copyFailed: string } | null;

/** Runs a channel. The sheet handles the results: the picture dialog (WeChat, QQ) and the manual-copy field. */
export async function runChannel(ch: ShareChannel, t: ShareTarget, o: RunOptions): Promise<RunResult> {
  if (isPicture(ch)) return { picture: ch };
  const url = withChannel(t.url, ch);
  switch (ch) {
    case 'sys': {
      const file = await (o.file ?? squareFile(t.images.sq, o.fileName));
      // AbortError (the player closed the system sheet) is not an error
      await navigator.share({ title: t.text, text: t.text, url, ...(file ? { files: [file] } : {}) }).catch(() => undefined);
      return null;
    }
    case 'cp':
      try {
        await navigator.clipboard.writeText(url);
        toast.success('share.copied');
        return null;
      } catch {
        return { copyFailed: url };
      }
    case 'dl':
      downloadImage(t.images.sq, o.fileName);
      return null;
    default: {
      const intent = channelUrl(ch, { url, text: t.text, image: t.images.og });
      if (intent) window.open(intent, '_blank', 'noopener,noreferrer');
      return null;
    }
  }
}
